import { useEffect, useMemo, useState } from "react";
import { DepartmentPlanDeal } from "../../../model/dashboard.types";
import {
  DealColumnId,
  DealGroupKey,
  DEFAULT_VISIBLE_COLUMNS,
  GROUP_KEY_ORDER,
  REQUIRED_VISIBLE_COLUMNS,
  getDealGroupValue,
  sumDealNumericField,
} from "./dealsDetailsColumns";

const VISIBLE_COLUMNS_KEY = "dealsDetails_visibleColumns";
const COLUMNS_SCHEMA_KEY = "dealsDetails_visibleColumns_schema";
const COLUMNS_SCHEMA_VERSION = 2;
const GROUP_BY_KEY = "dealsDetails_groupBy";

/** Столбцы, добавленные в конкретной версии схемы — подмешиваются при миграции */
const COLUMNS_ADDED_IN_VERSION: Record<number, DealColumnId[]> = {
  2: ["marginality"],
};

export type DealTableRow =
  | { type: "group"; level: number; groupKey: DealGroupKey; label: string; deals: DepartmentPlanDeal[] }
  | { type: "deal"; level: number; deal: DepartmentPlanDeal };

function normalizeVisibleColumns(columns: DealColumnId[]): DealColumnId[] {
  const allowed = new Set(columns);
  const normalized = DEFAULT_VISIBLE_COLUMNS.filter((id) => allowed.has(id));

  for (const requiredColumn of REQUIRED_VISIBLE_COLUMNS) {
    if (!normalized.includes(requiredColumn)) {
      normalized.push(requiredColumn);
    }
  }

  return DEFAULT_VISIBLE_COLUMNS.filter((id) => normalized.includes(id));
}

function migrateVisibleColumns(columns: DealColumnId[]): DealColumnId[] {
  const storedVersion = Number(localStorage.getItem(COLUMNS_SCHEMA_KEY) || "1");
  const next = new Set(columns);

  for (let version = storedVersion + 1; version <= COLUMNS_SCHEMA_VERSION; version++) {
    for (const columnId of COLUMNS_ADDED_IN_VERSION[version] ?? []) {
      next.add(columnId);
    }
  }

  localStorage.setItem(COLUMNS_SCHEMA_KEY, String(COLUMNS_SCHEMA_VERSION));
  return normalizeVisibleColumns(Array.from(next));
}

function readVisibleColumns(): DealColumnId[] {
  try {
    const raw = localStorage.getItem(VISIBLE_COLUMNS_KEY);
    if (!raw) {
      localStorage.setItem(COLUMNS_SCHEMA_KEY, String(COLUMNS_SCHEMA_VERSION));
      return DEFAULT_VISIBLE_COLUMNS;
    }
    const parsed = JSON.parse(raw) as DealColumnId[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      localStorage.setItem(COLUMNS_SCHEMA_KEY, String(COLUMNS_SCHEMA_VERSION));
      return DEFAULT_VISIBLE_COLUMNS;
    }
    return migrateVisibleColumns(
      parsed.filter((id) => DEFAULT_VISIBLE_COLUMNS.includes(id))
    );
  } catch {
    localStorage.setItem(COLUMNS_SCHEMA_KEY, String(COLUMNS_SCHEMA_VERSION));
    return DEFAULT_VISIBLE_COLUMNS;
  }
}

function readGroupBy(): DealGroupKey[] {
  try {
    const raw = localStorage.getItem(GROUP_BY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DealGroupKey[];
    if (!Array.isArray(parsed)) return [];
    return GROUP_KEY_ORDER.filter((key) => parsed.includes(key));
  } catch {
    return [];
  }
}

function buildGroupedRows(
  deals: DepartmentPlanDeal[],
  groupBy: DealGroupKey[],
  level = 0
): DealTableRow[] {
  if (groupBy.length === 0) {
    return deals.map((deal) => ({ type: "deal", level, deal }));
  }

  const [currentKey, ...rest] = groupBy;
  const groups = new Map<string, DepartmentPlanDeal[]>();

  for (const deal of deals) {
    const value = getDealGroupValue(deal, currentKey);
    const bucket = groups.get(value);
    if (bucket) {
      bucket.push(deal);
    } else {
      groups.set(value, [deal]);
    }
  }

  const rows: DealTableRow[] = [];
  const sortedKeys = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b, "ru"));

  for (const key of sortedKeys) {
    const groupDeals = groups.get(key)!;
    rows.push({
      type: "group",
      level,
      groupKey: currentKey,
      label: key,
      deals: groupDeals,
    });
    rows.push(...buildGroupedRows(groupDeals, rest, level + 1));
  }

  return rows;
}

export function useDealsDetailsView(deals: DepartmentPlanDeal[] | undefined) {
  const [visibleColumns, setVisibleColumns] = useState<DealColumnId[]>(readVisibleColumns);
  const [groupBy, setGroupBy] = useState<DealGroupKey[]>(readGroupBy);

  useEffect(() => {
    localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem(GROUP_BY_KEY, JSON.stringify(groupBy));
  }, [groupBy]);

  const rows = useMemo(() => {
    if (!deals?.length) return [];
    if (groupBy.length === 0) {
      return deals.map((deal) => ({ type: "deal" as const, level: 0, deal }));
    }
    return buildGroupedRows(deals, groupBy);
  }, [deals, groupBy]);

  const toggleColumn = (columnId: DealColumnId) => {
    if (REQUIRED_VISIBLE_COLUMNS.includes(columnId)) {
      return;
    }

    setVisibleColumns((prev) => {
      if (prev.includes(columnId)) {
        const next = prev.filter((id) => id !== columnId);
        return normalizeVisibleColumns(next);
      }
      return normalizeVisibleColumns([...prev, columnId]);
    });
  };

  const toggleGroup = (groupKey: DealGroupKey) => {
    setGroupBy((prev) => {
      if (prev.includes(groupKey)) {
        return prev.filter((key) => key !== groupKey);
      }
      return GROUP_KEY_ORDER.filter((key) => key === groupKey || prev.includes(key));
    });
  };

  const groupTotals = (groupDeals: DepartmentPlanDeal[]) => ({
    opportunity: sumDealNumericField(groupDeals, "opportunity"),
    profit: sumDealNumericField(groupDeals, "profit"),
    actual_paid: sumDealNumericField(groupDeals, "actual_paid"),
  });

  return {
    visibleColumns,
    groupBy,
    rows,
    toggleColumn,
    toggleGroup,
    groupTotals,
  };
}
